import { useCallback, useState } from 'react'
import type { JSX } from 'react'

import {
  RxGrid,
  committedAxis,
  committedDioptre,
  emptyGrid,
  entered,
  type GridState,
} from '@lensmira/ui'
import { dioptre, toNumber, validateAdd, validateAxis } from '@lensmira/domain'

import { useTranslation } from '../i18n.js'
import { statusLine } from '../queue/status-line.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'
import type { PrescriptionVersionView } from './CustomerDetail.js'

/**
 * Issuing a draft prescription and correcting an active one. `RX-002`; `decisions/0024`.
 *
 * Both are writes, so both go through the queue (`OFF-001a`): this component holds no transport.
 *
 * **A refusal from a queued write blocks the queue head and everything behind it**, so the form
 * refuses locally what the server would refuse and the shopkeeper can act on: no sphere, an axis
 * that breaks Appendix I, an add outside its range, no reason. The server still checks all of it
 * (a queue posts JSON), but a screen that lets a known-bad correction through turns a typo into a
 * stuck till.
 *
 * Issue is offered on a draft and Correct on an active version. Nothing else has an edge to
 * `superseded` (Appendix C.4), so nothing else is offered.
 */

export interface RxActionsProps {
  readonly rx: PrescriptionVersionView
  /** Called once the server has taken the write, so the parent can read the customer again. */
  readonly onSettled: () => void
}

function gridFrom(rx: PrescriptionVersionView): GridState {
  const grid = emptyGrid()
  for (const row of ['od', 'os'] as const) {
    const eye = rx[row]
    grid[row].sphere = entered(String(eye.sphere))
    grid[row].cylinder = entered(String(eye.cylinder))
    if (eye.axis !== undefined) grid[row].axis = entered(String(eye.axis))
    if (eye.add !== undefined) grid[row].add = entered(String(eye.add))
  }
  return grid
}

/** What the server would refuse, in words, or null when the form is sendable. */
function firstProblem(state: GridState, reason: string): string | null {
  if (reason.trim() === '') return 'reason'
  for (const row of ['od', 'os'] as const) {
    const sphere = committedDioptre(state[row].sphere)
    if (sphere === undefined) return 'sphere'
    const cylinder = committedDioptre(state[row].cylinder)
    const blocking = validateAxis(committedAxis(state[row].axis), cylinder ?? dioptre(0)).find(
      (f) => f.severity === 'block',
    )
    if (blocking) return blocking.message
    const add = committedDioptre(state[row].add)
    if (add !== undefined) {
      const addBlock = validateAdd(add).find((f) => f.severity === 'block')
      if (addBlock) return addBlock.message
    }
  }
  return null
}

export function RxActions({ rx, onSettled }: RxActionsProps): JSX.Element | null {
  const { t } = useTranslation()
  const { enqueue, snapshot } = useWriteQueue()
  const [open, setOpen] = useState(false)
  const [grid, setGrid] = useState<GridState>(() => gridFrom(rx))
  const [reason, setReason] = useState('')
  const [writeId, setWriteId] = useState<string | null>(null)

  useWriteSettled(snapshot, writeId, () => {
    setOpen(false)
    setReason('')
    onSettled()
  })

  const issue = useCallback(async () => {
    const write = (await enqueue({
      method: 'POST',
      path: `/prescriptions/${rx.id}/activate`,
      label: t('rxAction.issue'),
      body: {},
    })) as { id: string }
    setWriteId(write.id)
  }, [enqueue, rx.id, t])

  const eye = (row: 'od' | 'os'): Record<string, number | undefined> => {
    const sphere = committedDioptre(grid[row].sphere)
    const cylinder = committedDioptre(grid[row].cylinder)
    const add = committedDioptre(grid[row].add)
    return {
      sphere: sphere === undefined ? undefined : toNumber(sphere),
      cylinder: cylinder === undefined ? undefined : toNumber(cylinder),
      axis: committedAxis(grid[row].axis),
      add: add === undefined ? undefined : toNumber(add),
    }
  }

  const problem = firstProblem(grid, reason)

  const correct = useCallback(async () => {
    const write = (await enqueue({
      method: 'POST',
      path: `/prescriptions/${rx.id}/versions`,
      label: t('rxAction.correctLabel', { version: rx.version }),
      body: { od: eye('od'), os: eye('os'), reason: reason.trim() },
    })) as { id: string }
    setWriteId(write.id)
  }, [enqueue, grid, reason, rx.id, rx.version, t])

  if (rx.status === 'draft') {
    return (
      <>
        <button
          type="button"
          className="lm-button lm-button--secondary"
          onClick={() => {
            void issue()
          }}
        >
          {t('rxAction.issue')}
        </button>
        <p className="lm-hint">{t('rxAction.issueHint')}</p>
        <p className="lm-queue-state" role="status">
          {statusLine(t, snapshot, writeId, t('queue.subject.rxIssue'))}
        </p>
      </>
    )
  }

  if (rx.status !== 'active') return null

  if (!open) {
    return (
      <button
        type="button"
        className="lm-button lm-button--secondary"
        onClick={() => {
          setGrid(gridFrom(rx))
          setOpen(true)
        }}
      >
        {t('rxAction.correct')}
      </button>
    )
  }

  return (
    <form
      aria-label={t('rxAction.heading', { version: rx.version })}
      onSubmit={(e) => {
        e.preventDefault()
        if (problem === null) void correct()
      }}
    >
      <h4>{t('rxAction.heading', { version: rx.version })}</h4>
      <RxGrid value={grid} onChange={setGrid} />
      <label className="lm-field">
        <span className="lm-field__label">{t('rxAction.reason')}</span>
        <input
          className="lm-input"
          type="text"
          required
          value={reason}
          onChange={(e) => {
            setReason(e.currentTarget.value)
          }}
        />
      </label>
      {problem !== null && problem !== 'reason' && problem !== 'sphere' ? (
        <p className="lm-error" role="alert">
          {problem}
        </p>
      ) : null}
      {problem === 'sphere' ? (
        <p className="lm-error" role="alert">
          {t('rxAction.sphereRequired')}
        </p>
      ) : null}
      <div className="lm-screen__actions">
        <button type="submit" className="lm-button lm-button--primary" disabled={problem !== null}>
          {t('rxAction.submit')}
        </button>
        <button
          type="button"
          className="lm-button lm-button--secondary"
          onClick={() => {
            setOpen(false)
          }}
        >
          {t('rxAction.cancel')}
        </button>
      </div>
      <p className="lm-queue-state" role="status">
        {statusLine(t, snapshot, writeId, t('queue.subject.correction'))}
      </p>
    </form>
  )
}
